import { useEffect, useMemo, useRef, useState } from 'react';
import { breakpointsRaw } from '~/theme/breakpoints';
import { Brands } from '~/lib/data-contract';
import { getCarouselSettings } from './getCarouselSettings';

export function useGetCarouselOptions(items: Brands[]) {
    const totalItems = items.length;
    const { desktop, tablet, mobile } = getCarouselSettings(totalItems);
    const isOdd = totalItems % 2 !== 0;

    const useSlider = (totalItems === 6 && isOdd) || totalItems === 5 || totalItems > 6;

    const leftArrow = useRef<HTMLButtonElement | null>(null);
    const rightArrow = useRef<HTMLButtonElement | null>(null);

    const [isDesktop, setIsDesktop] = useState(false);
    const [isTablet, setIsTablet] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const md = window.matchMedia(`(min-width: ${breakpointsRaw.md}px)`);
        const sm = window.matchMedia(`(min-width: ${breakpointsRaw.sm}px)`);

        const updateSizes = () => {
            setIsDesktop(md.matches);
            setIsTablet(sm.matches && !md.matches);
            setIsMobile(!sm.matches);
        };

        updateSizes();
        md.addEventListener('change', updateSizes);
        sm.addEventListener('change', updateSizes);

        return () => {
            md.removeEventListener('change', updateSizes);
            sm.removeEventListener('change', updateSizes);
        };
    }, []);

    const isButtonHidden =
        (totalItems === 5 && isDesktop) || (totalItems === 3 && (isDesktop || isTablet));

    const shouldShowButtons = useSlider && !(isMobile || isButtonHidden);

    const carouselOptions = useMemo(() => {
        if (!useSlider && totalItems !== 3) {
            return {
                spaceBetween: 0,
                allowTouchMove: false,
                slidesPerView: mobile.slidePerView,
                grid: {
                    rows: mobile.numberOfRows,
                    fill: 'row' as const,
                },
                breakpoints: {
                    [breakpointsRaw.sm]: {
                        slidesPerView: tablet.slidePerView,
                        grid: {
                            fill: 'row' as const,
                            rows: tablet.numberOfRows,
                        },
                    },
                    [breakpointsRaw.md]: {
                        slidesPerView: desktop.slidePerView,
                        grid: {
                            fill: 'row' as const,
                            rows: desktop.numberOfRows,
                        },
                    },
                },
            };
        }

        return {
            spaceBetween: 0,
            allowTouchMove: true,
            observer: true,
            observeParents: true,
            slidesPerView: mobile.slidePerView,
            breakpoints: {
                [breakpointsRaw.sm]: {
                    slidesPerView: tablet.slidePerView,
                },
                [breakpointsRaw.md]: {
                    slidesPerView: desktop.slidePerView,
                },
            },
        };
    }, [useSlider, totalItems, mobile, tablet, desktop]);

    return {
        leftArrow,
        rightArrow,
        isMobile,
        isTablet,
        isDesktop,
        desktop,
        tablet,
        mobile,
        useSlider,
        shouldShowButtons,
        carouselOptions,
    };
}
